import { ChangeEvent, useEffect, useState } from "react";
import { ValidateFileInput } from "@src/config/helper";
import Accordion from "react-bootstrap/Accordion";
import {
    createRegisterUploadItem,
    getFieldDisplayLabel,
    getFieldKey,
    getUploadFileName,
    getUploadPreviewSrc,
    isUploadImageFile,
    revokeRegisterUploadPreview,
} from "./registerFormUtils";
import GuidePopupPopup from "./GuidePopup";

const isUploadPdfFile = (fileItem: any) => {
    const rawFile = fileItem?.file instanceof File ? fileItem.file : fileItem;

    if (rawFile instanceof File) {
        return rawFile.type === "application/pdf" || rawFile.name.toLowerCase().endsWith(".pdf");
    }

    if (typeof fileItem?.type === "string" && fileItem.type.toLowerCase().includes("pdf")) {
        return true;
    }

    const fileName = `${getUploadFileName(fileItem) || fileItem?.file_name || fileItem?.src || fileItem?.file_url || ""}`
        .split("?")[0]
        .toLowerCase();

    return fileName.endsWith(".pdf");
};

const getUploadOpenSrc = (fileItem: any) => {
    if (typeof fileItem === "string") {
        return fileItem;
    }

    return fileItem?.preview_url || fileItem?.src || fileItem?.file_url || "";
};

function RegisterDoc(props: any) {
    const { sectionItem, sectionIndex, updateSection, onNext } = props;
    const [activeRegisterSection, setActiveRegisterSection] = useState("");
    const docFields = (sectionItem?.blocks || []).flatMap((blockItem: any) => blockItem?.fields || []);
    const [showGuidePopup, setshowGuidePopup] = useState(false);
    useEffect(() => {
        if (!docFields.length) {
            return;
        }

        const firstFieldKey = getFieldKey(docFields[0]);
        const activeFieldExists = docFields.some((fieldItem: any, fieldIndex: number) => {
            return getFieldKey(fieldItem, fieldIndex) === activeRegisterSection;
        });

        if (!activeRegisterSection || !activeFieldExists) {
            setActiveRegisterSection(firstFieldKey);
        }
    }, [activeRegisterSection, docFields]);

    const toggleRegisterSection = (sectionKey: string) => {
        setActiveRegisterSection((prevState) => prevState === sectionKey ? "" : sectionKey);
    };

    const handleFileUpload = async (fieldItem: any, event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";

        if (!selectedFiles.length) {
            return;
        }

        const isFileInvalid = await ValidateFileInput(selectedFiles);
        if (isFileInvalid) {
            return;
        }

        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.id !== fieldItem?.id) {
                        return;
                    }

                    const previousFiles = currentField?.uploaded_files || [];
                    const maxUpload = currentField?.max_upload || previousFiles.length + selectedFiles.length;
                    const normalizedFiles = selectedFiles.map((selectedFile: File) => createRegisterUploadItem(selectedFile));
                    const combinedFiles = [...previousFiles, ...normalizedFiles];
                    const droppedFiles = combinedFiles.slice(maxUpload);
                    droppedFiles.forEach((fileItem: any) => revokeRegisterUploadPreview(fileItem));
                    currentField.uploaded_files = combinedFiles.slice(0, maxUpload);
                    currentField.error_message = "";
                });
            });

            return nextSection;
        });
    };

    const removeUploadedFile = (fieldItem: any, fileIndex: number) => {
        updateSection(sectionIndex, (currentSection: any) => {
            const nextSection = structuredClone(currentSection);

            nextSection?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((currentField: any) => {
                    if (currentField?.id !== fieldItem?.id) {
                        return;
                    }

                    const removedFile = currentField?.uploaded_files?.[fileIndex];
                    revokeRegisterUploadPreview(removedFile);
                    currentField.uploaded_files = (currentField?.uploaded_files || []).filter(
                        (_item: any, currentIndex: number) => currentIndex !== fileIndex
                    );
                    currentField.error_message = "";
                });
            });

            return nextSection;
        });
    };

    const openUploadedFile = (uploadedFile: any) => {
        const rawFile = uploadedFile?.file instanceof File ? uploadedFile.file : null;
        const fileUrl = rawFile ? URL.createObjectURL(rawFile) : getUploadOpenSrc(uploadedFile);

        if (!fileUrl) {
            return;
        }

        window.open(fileUrl, "_blank", "noopener,noreferrer");

        if (rawFile) {
            setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        }
    };

    const renderUploadedFileCard = (fieldItem: any, uploadedFile: any, fileIndex: number) => {
        const previewSrc = getUploadPreviewSrc(uploadedFile);
        const isImageUpload = isUploadImageFile(uploadedFile);
        const isPdfUpload = isUploadPdfFile(uploadedFile);

        return (
            <div
                key={`${getFieldKey(fieldItem, fileIndex)}_upload_${fileIndex}`}
                style={{
                    position: "relative",
                    width: "100%",
                    height: "150px",
                    minHeight: "150px",
                    background: "#fff",
                    border: "1px solid #D4E0EB",
                    borderRadius: "8px",
                    padding: "12px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center"
                }}
            >
                <button
                    type="button"
                    onClick={() => removeUploadedFile(fieldItem, fileIndex)}
                    aria-label={`Remove ${getFieldDisplayLabel(fieldItem)}`}
                    style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        width: "22px",
                        height: "22px",
                        border: "none",
                        borderRadius: "50%",
                        background: "#111827",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                    }}
                >
                    <i className="ic-clearclose" style={{ fontSize: "10px" }}></i>
                </button>

                <div className="d-flex flex-column align-items-center justify-content-center" style={{ width: "100%", height: "100%" }}>
                    {isImageUpload && previewSrc ? (
                        <img
                            src={previewSrc}
                            alt={getFieldDisplayLabel(fieldItem)}
                            onClick={() => openUploadedFile(uploadedFile)}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "6px",
                                cursor: "pointer"
                            }}
                        />
                    ) : (
                        <>
                            {isPdfUpload ? (
                                <span
                                    onClick={() => openUploadedFile(uploadedFile)}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <i
                                        className="ic-pdf-file icons-normal"
                                        style={{
                                            fontSize: "48px",
                                            color: "#008CF0",
                                            lineHeight: 1,
                                            display: "block",
                                            transform: "translateX(4px)",
                                        }}
                                    ></i>
                                </span>
                            ) : (
                                <>
                                    <i
                                        className="ic-insert_drive_file"
                                        onClick={() => openUploadedFile(uploadedFile)}
                                        style={{ fontSize: "28px", color: "#008CF0", marginBottom: "8px", cursor: "pointer" }}
                                    ></i>
                                    <span style={{ fontSize: "10px", lineHeight: "14px", color: "#111827", wordBreak: "break-word" }}>
                                        {getUploadFileName(uploadedFile)}
                                    </span>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="doc-upload-outer-id">
            {sectionItem?.blocks?.[0]?.title ? <div className="upload-sec-heading">{sectionItem.blocks[0].title}</div> : null}

            <div className="register-scroll-outer">
                {docFields.map((fieldItem: any, fieldIndex: number) => {
                    const fieldKey = getFieldKey(fieldItem, fieldIndex);
                    const uploadedFiles = fieldItem?.uploaded_files || [];
                    const isSectionOpen = activeRegisterSection === fieldKey;
                    const hasUploadedFiles = uploadedFiles.length > 0;
                    
                    return (
                        <div className="faq-wrapper resummison-outer document-upload-acc-outer">
                            <div className="document-card animation-effect">
                                <Accordion>
                                    <Accordion.Item eventKey={fieldKey}>

                                        {/* HEADER */}
                                        <div
                                            className="summery-heading upload-heading-text document-upload-id w-100"
                                            style={{ background: fieldItem?.color_code || "#D8EAFF" }}
                                        >
                                            <Accordion.Header>

                                                {/* LEFT SIDE */}

                                                <span className="doc-upload-icn">
                                                    {fieldItem?.logo ? (
                                                        <img
                                                            src={fieldItem.logo}
                                                            alt={fieldItem?.label || "document"}
                                                        />
                                                    ) : (
                                                        <i className="ic-insert_drive_file"></i>
                                                    )}
                                                </span>

                                                <div className="">
                                                    <h3>{getFieldDisplayLabel(fieldItem)}</h3>

                                                    {fieldItem?.show_guide && (
                                                        <span className="guide-txt" onClick={() => setshowGuidePopup(true)}>
                                                            Guide <i className="ic-keyboard_arrow_right"></i>
                                                        </span>
                                                    )}
                                                </div>



                                                {/* RIGHT SIDE */}
                                                {hasUploadedFiles ? (
                                                    <div className="check-filed-icn">
                                                        <i className="ic-check"></i>
                                                    </div>
                                                ) : null}
                                            </Accordion.Header>
                                        </div>

                                        {/* BODY */}
                                        <Accordion.Body>
                                            <div className="image-outer-sec" style={{ background: "#EEF7FF" }}>

                                                {/* GUIDE */}
                                                {fieldItem?.show_guide &&
                                                    (fieldItem?.title || fieldItem?.description) && (
                                                        <div>
                                                            {/* {fieldItem?.title && <div>{fieldItem.title}</div>} */}
                                                            {/* {fieldItem?.description && (
                                                                <div>{fieldItem.description}</div>
                                                            )} */}
                                                        </div>
                                                    )}

                                                {/* UPLOAD SECTION */}
                                                <div className="row">
                                                    {Array.from({ length: fieldItem?.max_upload || 1 }).map(
                                                        (_item, uploadIndex) => {
                                                            const uploadedFile = uploadedFiles[uploadIndex];

                                                            return (
                                                                <div
                                                                    key={`${fieldKey}_slot_${uploadIndex}`}
                                                                    className="col-md-4 col-xs-6 p-lg-t p-md-b"
                                                                >
                                                                    {!uploadedFile ? (
                                                                        <div className="add-document-proof">
                                                                            <input
                                                                                type="file"
                                                                                className="upload-image-input"
                                                                                accept="image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx"
                                                                                disabled={
                                                                                    uploadedFiles.length >=
                                                                                    (fieldItem?.max_upload || 1)
                                                                                }
                                                                                onChange={(event) =>
                                                                                    handleFileUpload(fieldItem, event)
                                                                                }
                                                                            />
                                                                            <i className="ic-add"></i>
                                                                            <span>Upload</span>
                                                                        </div>
                                                                    ) : (
                                                                        renderUploadedFileCard(
                                                                            fieldItem,
                                                                            uploadedFile,
                                                                            uploadIndex
                                                                        )
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>

                                                {/* ERROR */}
                                                {fieldItem?.error_message && (
                                                    <span className="error-txt">
                                                        {fieldItem.error_message}
                                                    </span>
                                                )}
                                            </div>
                                        </Accordion.Body>

                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="btn-bottom-fixed p-lg-t btn-full">
                <button type="button" className="btn-primary" onClick={onNext}>
                    {sectionItem?.btn?.title || "Continue"}
                </button>
            </div>
            <GuidePopupPopup
                show={showGuidePopup}
                handleClose={() => setshowGuidePopup(false)}
            />
        </div>
    );
}

export default RegisterDoc
